// BREAK BULK MANAGER
// Save as src/adminwork/BreakBulkManager.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BreakBulkManager.css';

const BreakBulkManager = () => {
  // States for vessels and loading
  const [vessels, setVessels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // States for all columns and visible columns
  const [allColumns, setAllColumns] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  
  // States for modals
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState(''); // 'add', 'edit', 'columns', 'newField', or 'editField'
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [formData, setFormData] = useState({});
  const [newFieldName, setNewFieldName] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const [newFieldNameForEdit, setNewFieldNameForEdit] = useState('');
  
  // Fetch schema (available columns) from API
  const fetchSchema = async () => {
    try {
      const response = await axios.get('https://site2.akms.org.in/api/break-bulk/schema');
      
      // Convert API response to our column format
      const columns = response.data.map(col => ({
        id: col.COLUMN_NAME,
        visible: true, // All columns visible by default
        type: col.DATA_TYPE
      }));
      
      setAllColumns(columns);
      setVisibleColumns(columns.map(col => col.id));
      
      return columns;
    } catch (error) {
      console.error('Error fetching schema:', error);
      setError('Failed to load table schema. Please try again.');
      return [];
    }
  };
  
  // Fetch vessels data from API
  const fetchVessels = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // First fetch the schema to know all available columns
      const columns = await fetchSchema();
      
      // Then fetch the vessels data
      const response = await axios.get('https://site2.akms.org.in/api/break-bulk');
      setVessels(response.data);
      
    } catch (error) {
      console.error('Error fetching vessels:', error);
      setError('Failed to load vessels. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Initial data fetch
  useEffect(() => {
    fetchVessels();
  }, []);
  
  // Format column name for display (snake_case to Title Case)
  const formatColumnName = (column) => {
    return column
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // ---- Modal Handlers ----
  
  // Open Add Vessel modal
  const handleAddVessel = () => {
    // Create empty form fields for all available columns
    const emptyForm = {};
    allColumns.forEach(column => {
      emptyForm[column.id] = '';
    });
    
    setFormData(emptyForm);
    setSelectedVessel(null);
    setModalMode('add');
    setShowModal(true);
  };
  
  // Open Edit Vessel modal
  const handleEditVessel = (vessel) => {
    // Copy vessel data to form
    const vesselForm = { ...vessel };
    
    // Add empty fields for any columns that aren't in this vessel
    allColumns.forEach(column => {
      if (!(column.id in vesselForm)) {
        vesselForm[column.id] = '';
      }
    });
    
    setFormData(vesselForm);
    setSelectedVessel(vessel);
    setModalMode('edit');
    setShowModal(true);
  };
  
  // Open Manage Columns modal
  const handleManageColumns = () => {
    setModalMode('columns');
    setShowModal(true);
  };
  
  // Open Add Field modal
  const handleAddField = () => {
    setNewFieldName('');
    setModalMode('newField');
    setShowModal(true);
  };

  // Open Edit Field modal
  const handleEditField = (fieldId) => {
    setSelectedField(fieldId);
    setNewFieldNameForEdit(formatColumnName(fieldId));
    setModalMode('editField');
    setShowModal(true);
  };

  // Delete field function
  const handleDeleteField = async (fieldId) => {
    // Prevent deleting essential fields
    const essentialFields = ['vessels_name', 'dead_weight', 'built', 'loa', 'gross_ton', 'bale_capacity', 'max_lift', 'rampway', 'flag'];
    if (essentialFields.includes(fieldId)) {
      alert('Cannot delete essential fields. These fields are required for the system to function properly.');
      return;
    }
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete the field "${formatColumnName(fieldId)}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      // Send request to delete field from database schema
      const response = await axios.delete(`https://site2.akms.org.in/api/break-bulk/schema/${fieldId}`);
      
      if (response.data.success) {
        // Refresh schema and vessels data
        await fetchVessels();
        
        // Show success message
        alert(`Field "${formatColumnName(fieldId)}" has been deleted successfully.`);
      } else {
        alert(`Failed to delete field: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error deleting field:', error);
      alert('Failed to delete field. Please try again.');
    }
  };
  
  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Toggle column visibility
  const toggleColumnVisibility = (columnId) => {
    // Update visible columns list
    if (visibleColumns.includes(columnId)) {
      setVisibleColumns(prev => prev.filter(id => id !== columnId));
    } else {
      setVisibleColumns(prev => [...prev, columnId]);
    }
    
    // Update column visibility property
    setAllColumns(prev => 
      prev.map(col => 
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  };
  
  // Add new field/column
  const handleAddNewField = async () => {
    if (!newFieldName || newFieldName.trim() === '') {
      return;
    }
    
    // Convert to snake_case
    const fieldId = newFieldName.trim().toLowerCase().replace(/\s+/g, '_');
    
    // Check if field already exists
    if (allColumns.some(col => col.id === fieldId)) {
      alert('This field already exists.');
      return;
    }
    
    try {
      // Send request to add field to database schema
      const response = await axios.post('https://site2.akms.org.in/api/break-bulk/schema', { 
        field_name: fieldId 
      });
      
      if (response.data.success) {
        // Refresh schema and vessels data
        await fetchVessels();
        
        // Close modal
        setShowModal(false);
      } else {
        alert(`Failed to add field: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error adding field:', error);
      alert('Failed to add field. Please try again.');
    }
  };

  // Save edited field name
  const handleSaveFieldName = async () => {
    if (!newFieldNameForEdit || newFieldNameForEdit.trim() === '') {
      return;
    }
    
    // Convert to snake_case
    const newFieldId = newFieldNameForEdit.trim().toLowerCase().replace(/\s+/g, '_');
    
    // Check if new name is same as old name
    if (newFieldId === selectedField) {
      setShowModal(false);
      return;
    }
    
    // Check if field already exists
    if (allColumns.some(col => col.id === newFieldId)) {
      alert('A field with this name already exists.');
      return;
    }
    
    try {
      // Send request to rename field in database schema
      const response = await axios.put(`https://site2.akms.org.in/api/break-bulk/schema/${selectedField}`, { 
        new_field_name: newFieldId 
      });
      
      if (response.data.success) {
        // Refresh schema and vessels data
        await fetchVessels();
        
        // Close modal
        setShowModal(false);
      } else {
        alert(`Failed to rename field: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error renaming field:', error);
      alert('Failed to rename field. Please try again.');
    }
  };
  
  // Save vessel (add or edit)
  const handleSaveVessel = async (e) => {
    e.preventDefault();
    
    try {
      const dataToSend = { ...formData };
      
      // For update operations, we need to explicitly send empty strings
      // instead of removing them, so fields can be cleared
      if (modalMode === 'add') {
        // For new records, remove empty fields
        Object.keys(dataToSend).forEach(key => {
          if (dataToSend[key] === '') {
            delete dataToSend[key];
          }
        });
        
        // Add new vessel
        await axios.post('https://site2.akms.org.in/api/break-bulk', dataToSend);
      } else if (modalMode === 'edit') {
        // For updates, keep empty strings to clear fields in the database
        // We don't remove empty fields here
        
        // Update vessel
        await axios.put(`https://site2.akms.org.in/api/break-bulk/${selectedVessel.id}`, dataToSend);
      }
      
      // Refresh vessel data
      fetchVessels();
      
      // Close modal
      setShowModal(false);
    } catch (error) {
      console.error('Error saving vessel:', error);
      setError('Failed to save vessel. Please try again.');
    }
  };
  
  // Delete vessel
  const handleDeleteVessel = async (vesselId) => {
    if (!window.confirm('Are you sure you want to delete this vessel?')) {
      return;
    }
    
    try {
      await axios.delete(`https://site2.akms.org.in/api/break-bulk/${vesselId}`);
      
      // Refresh vessel data
      fetchVessels();
    } catch (error) {
      console.error('Error deleting vessel:', error);
      setError('Failed to delete vessel. Please try again.');
    }
  };
  
  // ---- Render Functions ----
  
  // Render Add/Edit Vessel form
  const renderVesselForm = () => {
    return (
      <form onSubmit={handleSaveVessel}>
        <div className="form-grid">
          {allColumns.map(column => (
            <div className="form-field" key={column.id}>
              <label>{formatColumnName(column.id)}</label>
              <input
                type="text"
                name={column.id}
                value={formData[column.id] || ''}
                onChange={handleInputChange}
              />
            </div>
          ))}
        </div>
        
        <div className="modal-footer">
          <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>
            Cancel
          </button>
          <button type="submit" className="save-btn">
            Save
          </button>
        </div>
      </form>
    );
  };
  
  // Render Manage Columns form
  const renderColumnsForm = () => {
    return (
      <div className="columns-manager">
        <p>Select fields to display in the table:</p>
        
        <div className="columns-grid">
          {/* First row for column headers */}
          <div className="columns-header">Field Name</div>
          <div className="columns-header">Actions</div>
          
          {/* Field items */}
          {allColumns.map(column => (
            <React.Fragment key={column.id}>
              <div className="column-item-name">
                <label>
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(column.id)}
                    onChange={() => toggleColumnVisibility(column.id)}
                  />
                  <span>{formatColumnName(column.id)}</span>
                </label>
              </div>
              <div className="column-item-actions">
                <button 
                  type="button" 
                  className="edit-field-btn"
                  onClick={() => handleEditField(column.id)}
                >
                  Edit
                </button>
                <button 
                  type="button" 
                  className="delete-field-btn"
                  onClick={() => handleDeleteField(column.id)}
                >
                  Delete
                </button>
              </div>
            </React.Fragment>
          ))}
        </div>
        
        <button 
          type="button" 
          className="add-field-btn"
          onClick={handleAddField}
        >
          Add New Field
        </button>
        
        <div className="modal-footer">
          <button type="button" className="close-btn" onClick={() => setShowModal(false)}>
            Close
          </button>
        </div>
      </div>
    );
  };
  
  // Render Add New Field form
  const renderNewFieldForm = () => {
    return (
      <div className="new-field-form">
        <div className="form-field">
          <label>Enter field name (e.g., "engine_power"):</label>
          <input
            type="text"
            value={newFieldName}
            onChange={(e) => setNewFieldName(e.target.value)}
            placeholder="Enter field name"
          />
        </div>
        
        <div className="modal-footer">
          <button 
            type="button" 
            className="cancel-btn" 
            onClick={() => setShowModal(false)}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="ok-btn"
            onClick={handleAddNewField}
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  // Render Edit Field Name form
  const renderEditFieldForm = () => {
    return (
      <div className="edit-field-form">
        <div className="form-field">
          <label>Edit field name:</label>
          <input
            type="text"
            value={newFieldNameForEdit}
            onChange={(e) => setNewFieldNameForEdit(e.target.value)}
            placeholder="Enter new field name"
          />
          <p className="field-name-hint">Current field name: <strong>{formatColumnName(selectedField)}</strong></p>
        </div>
        
        <div className="modal-footer">
          <button 
            type="button" 
            className="cancel-btn" 
            onClick={() => setShowModal(false)}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="ok-btn"
            onClick={handleSaveFieldName}
          >
            Save
          </button>
        </div>
      </div>
    );
  };
  
  // Main render
  return (
    <div className="break-bulk-container">
      <div className="header-section">
        <h2>Break Bulk Manager</h2>
        <div className="header-buttons">
          <button className="manage-columns-btn" onClick={handleManageColumns}>
            Manage Fields
          </button>
          <button className="add-vessel-btn" onClick={handleAddVessel}>
            Add New Vessel
          </button>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {isLoading ? (
        <div className="loading-message">Loading vessels...</div>
      ) : (
        <div className="vessels-table-wrapper">
          <table className="vessels-table">
            <thead>
              <tr>
                {visibleColumns.map(columnId => (
                  <th key={columnId}>{formatColumnName(columnId)}</th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vessels.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="no-data">
                    No vessels found. Add your first vessel to get started.
                  </td>
                </tr>
              ) : (
                vessels.map(vessel => (
                  <tr key={vessel.id}>
                    {visibleColumns.map(columnId => (
                      <td key={`${vessel.id}-${columnId}`}>
                        {vessel[columnId] || ''}
                      </td>
                    ))}
                    <td className="action-buttons">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditVessel(vessel)}
                      >
                        Edit
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteVessel(vessel.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Modal Dialog */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>
                {modalMode === 'add' ? 'Add New Vessel' : 
                 modalMode === 'edit' ? 'Edit Vessel' : 
                 modalMode === 'columns' ? 'Manage Fields' :
                 modalMode === 'newField' ? 'Add New Field' :
                 'Edit Field Name'}
              </h3>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {modalMode === 'add' || modalMode === 'edit' ? renderVesselForm() : 
               modalMode === 'columns' ? renderColumnsForm() : 
               modalMode === 'newField' ? renderNewFieldForm() :
               renderEditFieldForm()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BreakBulkManager;